import React, { useState } from "react";
import { isFunction, noop } from "lodash";
import { bool, func, object, oneOfType, shape, string } from "prop-types";

import ContactFormSchema from "src/shared/utilities/contactFormSchema";
import { useCurrentUser } from "src/shared/utilities/customHooks";

import { BUY_OR_SELL } from "../../constants/formTypes";
import { useDrawerContext } from "../../utilities/drawerHelpers";
import getInitialFormValues from "../../utilities/getInitialFormValues";
import useActiveExperts from "../../utilities/hooks/useActiveExperts";
import useContactExpert from "../../utilities/hooks/useContactExpert";
import useFeatureState from "../../utilities/hooks/useFeatureState";
import useMessageFlagsQuery from "../../utilities/hooks/useMessageFlagsQuery";
import useUserProfileQuery from "../../utilities/hooks/useUserProfileQuery";
import triggerConfirmationDialog from "../../utilities/triggerConfirmationDialog";
import useReducerActions from "../../utilities/useReducerActions";
import ContactForm from "../ContactForm";
import ExpertModal from "../ExpertModal";
import FormErrorCaught from "../FormErrorCaught";
import FormLoading from "../FormLoading";
import Header from "../Header";
import MessageScreening from "../MessageScreening";
import MultiStepForm from "../MultiStepForm";
import PostSubmitQuestionnaire from "../PostSubmitQuestionnaire";
import { withSentryErrorBoundary } from "../SentryErrorBoundaryLog";
import * as S from "./styled";

const onFormSubmit = (triggerSubmitModal, onSubmit) => async (
  submittedFormValues,
  { resetForm: resetFormikForm }
) => {
  resetFormikForm();

  if (onSubmit && isFunction(onSubmit)) {
    await onSubmit();
  }

  triggerSubmitModal(submittedFormValues);
};

const ExpertsForm = ({
  className,
  initialErrors,
  innerRef,
  isModal,
  onCancel,
  onSubmit
}) => {
  const hasDrawerForm = useDrawerContext();
  const { contactFormType } = useFeatureState();
  const {
    building,
    expert,
    listing,
    loading: activeExpertLoading
  } = useActiveExperts();
  const onExpertsSubmit = useContactExpert();
  const currentUser = useCurrentUser();
  const getMessageFlags = useMessageFlagsQuery();
  const [showMessageScreening, setShowMessageScreening] = useState(false);
  const onHdpPage = Boolean(listing);
  const [useDefaultHdpFlow, setUseDefaultHdpFlow] = useState(onHdpPage);
  const { dispatchSetMessageScreeningFlag } = useReducerActions();
  const {
    loading: userProfileLoading,
    showPostSubmitQuestionnaire
  } = useUserProfileQuery();

  const triggerSubmitModal = ({ email }) => {
    let confirmationHeader = null;
    let confirmationText = `We passed your note about ${building.address.pretty_address} on to ${expert.contact.name}`;

    if (onHdpPage) {
      if (showMessageScreening) {
        const agentName = useDefaultHdpFlow
          ? expert.contact.name
          : listing.contacts[0].name;

        confirmationHeader = "Message Sent";
        confirmationText = (
          <S.ConfirmationTextContainer>
            <b>What’s next?</b>
            <br />
            You’re all set. {agentName} will be in touch shortly.
          </S.ConfirmationTextContainer>
        );
      } else {
        confirmationText = `We passed your note about ${listing.address.pretty_address} ${listing.address.unit} on to ${expert.contact.name}`;
      }
    }

    if (contactFormType === BUY_OR_SELL) {
      confirmationHeader = "Message Sent!";
      confirmationText = `Your message is on its way to ${expert.contact.name}. Expect a confirmation email from us shortly with what's next.`;
    }

    return triggerConfirmationDialog({
      confirmationHeader,
      confirmationText,
      email,
      origin: "contact_agent"
    });
  };

  const onContactFormSubmit = async (values, { setFieldError }) => {
    if (onHdpPage) {
      const result = await getMessageFlags(values);

      if (result.showMessageScreening) {
        setShowMessageScreening(true);
        dispatchSetMessageScreeningFlag(result.flag);
      } else if (result.error) {
        setFieldError("form_submission", "Failed to deliver message");
      }

      if (result.showMessageScreening || result.error) {
        return result;
      }
    }

    return onExpertsSubmit(values, { setFieldError });
  };

  if (activeExpertLoading || !building || !currentUser || userProfileLoading)
    return <FormLoading className={className} isModal={isModal} />;

  if (!expert) {
    return <FormErrorCaught isModal={isModal} />;
  }

  const initialValues = getInitialFormValues({
    building,
    contactFormType,
    currentUser,
    expert,
    listing
  });

  const contactHeader =
    hasDrawerForm || !isModal ? <Header isModal={isModal} /> : null;

  return (
    <>
      <ExpertModal isModal={isModal} />
      <MultiStepForm
        className={className}
        formikProps={{
          initialErrors,
          validateOnBlur: false,
          validateOnChange: false,
          validationSchema: ContactFormSchema
        }}
        initialValues={initialValues}
        isModal={isModal}
        onCancel={onCancel}
        onSubmit={onFormSubmit(triggerSubmitModal, onSubmit)}
      >
        <ContactForm
          contactHeader={contactHeader}
          listing={listing}
          onSubmit={onContactFormSubmit}
          submitText="Send Message"
        />
        {showMessageScreening && (
          <MessageScreening
            showDrawerButtons={false}
            switchHdpFlow={() => setUseDefaultHdpFlow(false)}
          />
        )}
        {useDefaultHdpFlow && showPostSubmitQuestionnaire && (
          <PostSubmitQuestionnaire
            ref={innerRef}
            agentName={expert.contact.name}
            onCancel={onCancel}
            showDrawerButtons={false}
          />
        )}
      </MultiStepForm>
    </>
  );
};

ExpertsForm.propTypes = {
  className: string,
  // eslint-disable-next-line react/forbid-prop-types
  initialErrors: object,
  innerRef: oneOfType([func, shape({ current: object })]),
  isModal: bool,
  onCancel: func,
  onSubmit: func
};

ExpertsForm.defaultProps = {
  className: "",
  initialErrors: null,
  innerRef: null,
  isModal: false,
  onCancel: noop,
  onSubmit: noop
};

export default withSentryErrorBoundary(ExpertsForm);
